import type { Locator } from 'playwright';

export interface FilterableRoleLocator<TLocator> {
  filter(options: { hasText?: string }): TLocator;
}

export interface RoleLocatorContext<TLocator extends FilterableRoleLocator<TLocator> = Locator> {
  locator(selector: string): TLocator;
}

export interface NonQueryableRoleResolution<TLocator = Locator> {
  locator: TLocator;
  selectorDesc: string;
}

export interface AmbiguousNonQueryableRoleMatch {
  role: string;
  count: number;
  context: string;
}

export interface NonQueryableCandidateLocator<TElement, TResolvedLocator> {
  first(): TResolvedLocator;
  nth(index: number): TResolvedLocator;
  evaluateAll<TResult>(callback: (elements: TElement[]) => TResult): Promise<TResult>;
}

export interface ContainmentElement<TElement> {
  contains(other: TElement): boolean;
}

export interface ResolveNonQueryableRoleCandidateOptions<
  TElement extends ContainmentElement<TElement>,
  TResolvedLocator,
> {
  scopedLocator: NonQueryableCandidateLocator<TElement, TResolvedLocator>;
  selectorDesc: string;
  role: string;
  count: number;
  contextName: string;
  onAmbiguous?: (match: AmbiguousNonQueryableRoleMatch) => void;
}

/**
 * Roles Playwright's role engine cannot query: implicit `generic` elements
 * (plain divs/spans) are excluded from `getByRole` matching entirely, and
 * presentational roles are stripped from the accessibility tree.
 */
export const NON_QUERYABLE_ROLES: ReadonlySet<string> = new Set([
  'generic',
  'presentation',
  'none',
]);

export const isNonQueryableRole = (role: string): boolean => NON_QUERYABLE_ROLES.has(role);

/**
 * CSS approximation for elements whose computed role is `generic`: the
 * common implicit-generic containers plus any explicit role attribute.
 * Deliberately narrow: widening the tag set only adds ambiguity for the
 * innermost reduction to reject.
 */
const genericRoleCssApproximation = (role: string): string => `div, span, [role="${role}"]`;

export const buildNonQueryableRoleLocator = <TLocator extends FilterableRoleLocator<TLocator>>(
  context: RoleLocatorContext<TLocator>,
  role: string,
  activeContainsText: readonly string[] | undefined
): TLocator | null => {
  if (!activeContainsText || activeContainsText.length === 0) return null;

  let locator = context.locator(genericRoleCssApproximation(role));
  for (const part of activeContainsText) {
    if (!part) continue;
    locator = locator.filter({ hasText: part });
  }

  return locator;
};

export const resolveNonQueryableRoleCandidate = async <
  TElement extends ContainmentElement<TElement>,
  TResolvedLocator,
>({
  scopedLocator,
  selectorDesc,
  role,
  count,
  contextName,
  onAmbiguous,
}: ResolveNonQueryableRoleCandidateOptions<
  TElement,
  TResolvedLocator
>): Promise<NonQueryableRoleResolution<TResolvedLocator> | null> => {
  if (count === 0) return null;

  if (count === 1) {
    return { locator: scopedLocator.first(), selectorDesc };
  }

  // hasText matches subtrees, so every ancestor of the target also matched.
  // The substrate's exact-child-text-set uniqueness means the authored element
  // is the unique innermost match; multiple innermost matches are ambiguous.
  const innermostIndex = await scopedLocator.evaluateAll((elements) => {
    const innermost = elements.filter(
      (element) => !elements.some((other) => other !== element && element.contains(other))
    );
    return innermost.length === 1 ? elements.indexOf(innermost[0]) : -1;
  });

  if (innermostIndex >= 0) {
    return {
      locator: scopedLocator.nth(innermostIndex),
      selectorDesc: `${selectorDesc} [innermost]`,
    };
  }

  onAmbiguous?.({ role, count, context: contextName });
  return null;
};
